import React from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSite } from "../lib/site-context";
import TurnstileWidget from "../components/TurnstileWidget";

export default function KontaktPage() {
  const { settings, t } = useSite();
  const c = settings?.contact || {};

  const contact = {
    company: c.company || c.legal_name || "Dr. B.-Digitec GmbH",
    street: c.street || "Max-Planck-Straße 10",
    zip: c.zip || "50858",
    city: c.city || "Köln",
    country: c.country || t("Deutschland", "Germany"),
    phone: c.phone || "+49 221 292 423 80",
    mobile: c.mobile || "+49 155 6102 77 00",
    email: c.email || "info@dr-b-digitec.com",
  };

  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [security, setSecurity] = React.useState({ token: "", website: "" });
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [turnstileReset, setTurnstileReset] = React.useState(0);
  const [form, setForm] = React.useState({
    name: "",
    praxisname: "",
    email: "",
    phone: "",
    scanner_status: "",
    message: "",
    consent: false,
  });

  const setField = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const loadToken = React.useCallback(async () => {
    try {
      const result = await api.formToken("contact");
      setSecurity((old) => ({ ...old, token: result.token }));
    } catch {
      setErr(t("Das Formular konnte nicht vorbereitet werden. Bitte laden Sie die Seite neu.", "The form could not be prepared. Please reload the page."));
    }
  }, [t]);

  React.useEffect(() => { loadToken(); }, [loadToken]);

  async function submit(e) {
    e.preventDefault();
    setErr("");

    if (!form.name || !form.email || !form.message || !form.consent) {
      setErr(t("Bitte füllen Sie Name, E-Mail, Nachricht und die Datenschutz-Bestätigung aus.", "Please complete name, email, message and privacy confirmation."));
      return;
    }

    setBusy(true);
    try {
      await api.submitPraxisCheck({ ...form, type: "contact", concern: "Kontaktseite", _form_token: security.token, _website: security.website, _turnstile_token: turnstileToken });
      setSent(true);
      setForm({ name: "", praxisname: "", email: "", phone: "", scanner_status: "", message: "", consent: false });
    } catch (error) {
      setErr(error?.response?.data?.detail || t("Die Anfrage konnte nicht gesendet werden. Bitte schreiben Sie uns direkt per E-Mail.", "The request could not be sent. Please email us directly."));
      await loadToken();
      setTurnstileReset((value) => value + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="contact-page bg-[var(--bg)] text-[var(--ink)]">
      <section className="brut-section pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-10 xl:col-span-8">
            <div className="brut-tag mb-6">{t("Kontakt", "Contact")}</div>
            <h1 className="brut-h1">{t("Sprechen wir über Ihren digitalen Workflow.", "Let's talk about your digital workflow.")}</h1>
            <p className="brut-body max-w-2xl mt-7">
              {t("Sie möchten wissen, ob Ihr Scanner, Ihre Praxisabläufe oder ein konkreter Fall zu Dr. B.-Digitec passen? Schicken Sie uns eine kurze Anfrage. Wir melden uns persönlich zurück.", "Would you like to know whether your scanner, practice workflow or a specific case is a good fit for Dr. B.-Digitec? Send us a short request. We will get back to you personally.")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={`mailto:${contact.email}`} className="brut-btn !py-3 !px-5">{t("E-Mail schreiben", "Send email")}</a>
              <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="brut-btn brut-btn-ghost !py-3 !px-5">{t("Anrufen", "Call")}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="brut-section py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <div className="admin-card p-6 md:p-8">
              {sent ? (
                <div className="py-12 text-center">
                  <h2 className="brut-h2">{t("Anfrage gesendet", "Request sent")}</h2>
                  <p className="brut-body max-w-xl mx-auto mt-4">{t("Vielen Dank. Wir melden uns persönlich bei Ihnen zurück.", "Thank you. We will get back to you personally.")}</p>
                  <button onClick={() => setSent(false)} className="brut-btn mt-8">{t("Weitere Anfrage senden", "Send another request")}</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="absolute -left-[10000px] w-px h-px overflow-hidden" aria-hidden="true">
                    <label>Website<input tabIndex={-1} autoComplete="off" value={security.website} onChange={(e) => setSecurity((old) => ({ ...old, website: e.target.value }))} /></label>
                  </div>
                  <div>
                    <div className="brut-tag mb-4">{t("Kontaktformular", "Contact form")}</div>
                    <h2 className="brut-h2">{t("Ihre Anfrage", "Your request")}</h2>
                    <p className="brut-body mt-3 opacity-75">{t("Bitte keine Patienten-, Gesundheits- oder Scandaten über dieses Formular senden.", "Please do not send patient, health or scan data through this form.")}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <label><span className="brut-label">{t("Name *", "Name *")}</span><input className="brut-input" value={form.name} onChange={(e) => setField("name", e.target.value)} /></label>
                    <label><span className="brut-label">{t("Praxis / Unternehmen", "Practice / Company")}</span><input className="brut-input" value={form.praxisname} onChange={(e) => setField("praxisname", e.target.value)} /></label>
                    <label><span className="brut-label">{t("E-Mail *", "Email *")}</span><input type="email" className="brut-input" value={form.email} onChange={(e) => setField("email", e.target.value)} /></label>
                    <label><span className="brut-label">{t("Telefon", "Phone")}</span><input className="brut-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} /></label>
                  </div>

                  <label><span className="brut-label">{t("Scanner / System optional", "Scanner / system optional")}</span><input className="brut-input" placeholder="3Shape, Medit, Primescan, iTero ..." value={form.scanner_status} onChange={(e) => setField("scanner_status", e.target.value)} /></label>
                  <label><span className="brut-label">{t("Nachricht *", "Message *")}</span><textarea rows={7} className="brut-textarea" value={form.message} onChange={(e) => setField("message", e.target.value)} /></label>

                  <label className="flex gap-3 items-start text-sm leading-relaxed">
                    <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => setField("consent", e.target.checked)} />
                    <span>{t("Ich habe die ", "I have read the ")}<Link to="/datenschutz" className="underline font-bold">{t("Datenschutzerklärung", "privacy policy")}</Link>{t(" gelesen und bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden.", " and agree that my information may be processed to handle my request.")}</span>
                  </label>

                  <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileReset} language={t("de", "en")} />

                  {err && <div className="border-2 border-red-500 p-3 text-sm font-bold">{err}</div>}
                  <button disabled={busy || !security.token || !turnstileToken} className="brut-btn w-full md:w-auto">{busy ? t("Wird gesendet …", "Sending …") : t("Anfrage senden", "Send request")}</button>
                </form>
              )}
            </div>
          </div>

          <aside className="col-span-12 lg:col-span-5">
            <div className="admin-card p-6 md:p-8 sticky top-28">
              <div className="brut-tag mb-5">{t("Direkter Kontakt", "Direct contact")}</div>
              <h2 className="brut-h2">{contact.company}</h2>
              <div className="brut-body space-y-5 mt-8">
                <p>{contact.street}<br />{contact.zip} {contact.city}<br />{contact.country}</p>
                <p><strong>{t("Telefon", "Phone")}:</strong><br /><a className="underline" href={`tel:${contact.phone.replace(/\s/g, "")}`}>{contact.phone}</a></p>
                <p><strong>{t("Mobil", "Mobile")}:</strong><br /><a className="underline" href={`tel:${contact.mobile.replace(/\s/g, "")}`}>{contact.mobile}</a></p>
                <p><strong>{t("E-Mail", "Email")}:</strong><br /><a className="underline" href={`mailto:${contact.email}`}>{contact.email}</a></p>
              </div>
              <div className="mt-8 border-t border-[var(--line-soft)] pt-6 text-sm opacity-75">
                {t("Für Falldaten, Scandaten und patientenbezogene Informationen wird ein gesicherter Übertragungsweg bereitgestellt.", "A secure transfer channel is provided for case data, scan data and patient-related information.")}
              </div>
              <Link to="/praxis-check" className="brut-btn brut-btn-ghost mt-8 w-full justify-center">{t("Praxis-Check starten", "Start Practice-Check")}</Link>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
